// «Черри» — the assistant's face. Pure animation logic, no React and no DOM:
// every feature (eyes, lids, brows, mouth, cheeks, head) is a damped spring
// that chases a target pose. The target is the emotion's preset, plus the
// current mode (listening / thinking / speaking), plus life: blinks, glances,
// breathing, the mouth following the real sound of the voice. CherryFace.js
// turns the pose into SVG every animation frame.

export const EMOTIONS = [
  'neutral',
  'happy',
  'joy',
  'excited',
  'love',
  'proud',
  'playful',
  'curious',
  'thinking',
  'surprised',
  'confused',
  'sad',
  'worried',
  'shy',
  'sleepy',
  'grumpy',
];

const BASE = {
  eyeOpen: 1,
  eyeScale: 1,
  happyEye: 0,
  wink: 0,
  lidAngle: 0,
  lidDrop: 0,
  squint: 0,
  shine: 1,
  browY: 0,
  browTilt: 0,
  browAsym: 0,
  smile: 0.32,
  mouthOpen: 0,
  mouthWide: 1,
  mouthRound: 0,
  mouthSkew: 0,
  tongue: 0,
  blush: 0.35,
  tilt: 0,
  lookX: 0,
  lookY: 0,
  bounce: 0,
  shrink: 0,
  sparkle: 0,
  hearts: 0,
  tear: 0,
  sweat: 0,
  zzz: 0,
  question: 0,
  dots: 0,
  notes: 0,
  waves: 0,
};

const PRESET = {
  neutral: {},
  happy: { smile: 0.78, squint: 0.28, blush: 0.55, browY: 0.18 },
  joy: {
    happyEye: 1,
    smile: 1,
    mouthOpen: 0.5,
    blush: 0.7,
    bounce: 0.55,
    browY: 0.35,
  },
  excited: {
    eyeScale: 1.16,
    shine: 1.35,
    smile: 0.95,
    mouthOpen: 0.42,
    browY: 0.65,
    sparkle: 1,
    bounce: 1,
    blush: 0.6,
  },
  love: {
    happyEye: 0.9,
    smile: 0.72,
    blush: 1,
    hearts: 1,
    tilt: -7,
    browY: 0.22,
  },
  proud: {
    smile: 0.72,
    squint: 0.4,
    lidDrop: 0.18,
    browY: 0.4,
    tilt: 5,
    sparkle: 0.55,
    lookY: -0.2,
  },
  playful: {
    wink: 1,
    smile: 0.62,
    mouthSkew: 0.55,
    browAsym: 0.6,
    tongue: 0.85,
    mouthOpen: 0.16,
    tilt: 8,
    notes: 0.8,
  },
  curious: {
    eyeScale: 1.09,
    browAsym: 0.75,
    smile: 0.22,
    mouthRound: 0.45,
    mouthOpen: 0.12,
    tilt: -9,
    lookX: 0.22,
  },
  thinking: {
    lookX: 0.55,
    lookY: -0.62,
    browAsym: 0.55,
    browTilt: 0.15,
    smile: 0.02,
    mouthSkew: -0.6,
    mouthWide: 0.72,
    dots: 1,
    tilt: 5,
  },
  surprised: {
    eyeScale: 1.26,
    shine: 1.2,
    browY: 1,
    mouthRound: 1,
    mouthOpen: 0.72,
    mouthWide: 0.62,
    smile: 0.08,
  },
  confused: {
    browAsym: 1,
    browTilt: 0.35,
    mouthSkew: 0.85,
    smile: -0.12,
    question: 1,
    tilt: -10,
    lookX: -0.22,
  },
  sad: {
    lidAngle: 17,
    browTilt: 0.95,
    browY: 0.12,
    smile: -0.62,
    tear: 1,
    lookY: 0.42,
    blush: 0.15,
    eyeScale: 1.05,
    shrink: 0.03,
  },
  worried: {
    browTilt: 0.85,
    browY: 0.38,
    smile: -0.32,
    mouthWide: 0.78,
    sweat: 1,
    lidAngle: 7,
    mouthOpen: 0.08,
  },
  shy: {
    squint: 0.32,
    lidDrop: 0.12,
    blush: 1,
    smile: 0.36,
    lookX: -0.45,
    lookY: 0.48,
    tilt: -11,
    shrink: 0.06,
    mouthWide: 0.7,
  },
  sleepy: {
    lidDrop: 0.66,
    smile: 0.08,
    mouthRound: 0.55,
    mouthOpen: 0.1,
    zzz: 1,
    tilt: 9,
    browY: -0.22,
    shine: 0.6,
  },
  grumpy: {
    lidAngle: -15,
    browTilt: -0.95,
    browY: -0.38,
    smile: -0.48,
    mouthWide: 0.74,
    lidDrop: 0.24,
    blush: 0.2,
  },
};

// Modes add to the emotion instead of replacing it.
const MODE = {
  idle: {},
  listening: { eyeScale: 0.07, browY: 0.28, tilt: -4, waves: 1 },
  thinking: { dots: 1, browAsym: 0.4 },
  speaking: {},
};

// stiffness / damping ratio per feature: eyes and gaze snappy, head slow and
// swaying, the brows a little bouncy, mouth fast enough for syllables.
const SPRING = {
  default: [140, 0.72],
  eyeScale: [230, 0.5],
  shine: [120, 0.8],
  lookX: [260, 0.88],
  lookY: [260, 0.88],
  browY: [190, 0.6],
  browTilt: [170, 0.65],
  browAsym: [170, 0.65],
  mouthOpen: [900, 0.9],
  mouthRound: [420, 0.85],
  mouthWide: [380, 0.8],
  smile: [200, 0.7],
  tilt: [55, 0.55],
  bounce: [60, 1],
  shrink: [120, 0.6],
  sparkle: [45, 1],
  hearts: [40, 1],
  tear: [35, 1],
  sweat: [35, 1],
  zzz: [35, 1],
  question: [60, 0.8],
  dots: [60, 1],
  notes: [40, 1],
  waves: [80, 1],
};

class Spring {
  constructor(x, k, zeta) {
    this.x = x;
    this.v = 0;
    this.k = k;
    this.c = 2 * Math.sqrt(k) * zeta;
  }

  step(target, dt) {
    const n = Math.max(1, Math.ceil(dt / (1 / 240)));
    const h = dt / n;
    for (let i = 0; i < n; i++) {
      const a = this.k * (target - this.x) - this.c * this.v;
      this.v += a * h;
      this.x += this.v * h;
    }
    return this.x;
  }
}

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (x, a, b) => Math.min(b, Math.max(a, x));

export class FaceRig {
  constructor({ calm = false } = {}) {
    this.calm = calm; // prefers-reduced-motion: smaller idle movement
    this.emotion = 'neutral';
    this.mode = 'idle';
    this.springs = {};
    Object.keys(BASE).forEach((k) => {
      const [stiff, zeta] = SPRING[k] || SPRING.default;
      this.springs[k] = new Spring(BASE[k], stiff, zeta);
    });
    this.pop = new Spring(1, 260, 0.38); // whole-head squash & stretch
    this.nod = new Spring(0, 180, 0.45);
    this.stem = new Spring(0, 38, 0.22);
    this.blink = { next: rand(1.2, 3.5), phase: -1, double: false };
    this.gaze = { x: 0, y: 0, next: rand(0.6, 2) };
    this.pointer = { x: 0, y: 0, at: -10 };
    this.env = 0; // smoothed loudness of the voice
    this.round = 0;
    this.time = 0;
    this.shake = 0;
    this.nextNod = rand(1.4, 2.6);
    this.hearts = [];
    this.heartClock = 0;
    this.bouncePhase = 0;
    this.prevTilt = 0;
    this.prevHx = 0;
  }

  setEmotion(name) {
    const e = PRESET[name] ? name : 'neutral';
    if (e === this.emotion) return;
    this.emotion = e;
    this.pop.v +=
      e === 'surprised' || e === 'excited' || e === 'joy' ? 2.6 : 1.2;
    if (Math.random() < 0.55) this.blink.next = this.time + 0.06;
  }

  setMode(mode) {
    const m = MODE[mode] ? mode : 'idle';
    if (m === this.mode) return;
    if (m === 'listening') this.pop.v += 1.4;
    if (m === 'thinking') this.gaze.next = this.time;
    this.mode = m;
  }

  setPointer(x, y) {
    this.pointer = {
      x: clamp(x, -1.2, 1.2),
      y: clamp(y, -1.2, 1.2),
      at: this.time,
    };
  }

  react(kind) {
    if (kind === 'nod') this.nod.v += 55;
    else if (kind === 'pop') this.pop.v += 2.4;
    else if (kind === 'shake') this.shake = 0.55;
    else if (kind === 'jump') {
      this.pop.v -= 2;
      this.nod.v -= 140;
    }
  }

  /* dt: seconds since the last frame; voice: { level 0..1, centroid Hz } of
     what the face is saying; mic: loudness of the person talking 0..1. */
  update(dt, { voice = null, mic = 0 } = {}) {
    dt = Math.min(dt, 1 / 20);
    this.time += dt;
    const t = this.time;
    const tgt = { ...BASE, ...PRESET[this.emotion] };
    const mode = MODE[this.mode];
    Object.keys(mode).forEach((k) => {
      tgt[k] =
        k === 'dots' || k === 'waves'
          ? Math.max(tgt[k], mode[k])
          : tgt[k] + mode[k];
    });
    if (this.mode === 'listening') tgt.smile = Math.max(tgt.smile, 0.3);

    // ── gaze: glances around, follows the pointer, looks up while thinking ──
    const thinking = this.mode === 'thinking' || this.emotion === 'thinking';
    if (t >= this.gaze.next) {
      const talking = this.mode === 'speaking' || this.mode === 'listening';
      if (thinking) {
        this.gaze.x = rand(-0.12, 0.12);
        this.gaze.y = rand(-0.1, 0.08);
        this.gaze.next = t + rand(0.7, 1.6);
      } else if (Math.random() < (talking ? 0.6 : 0.3)) {
        this.gaze.x = 0;
        this.gaze.y = 0;
        this.gaze.next = t + rand(0.9, 2.6);
      } else {
        this.gaze.x = rand(-0.38, 0.38);
        this.gaze.y = rand(-0.26, 0.22);
        this.gaze.next = t + rand(0.5, 2.2);
      }
    }
    const followPointer = t - this.pointer.at < 2.5 && !thinking;
    if (thinking && this.mode === 'thinking') {
      tgt.lookX = 0.55 + this.gaze.x;
      tgt.lookY = -0.6 + this.gaze.y;
    } else if (followPointer) {
      tgt.lookX = tgt.lookX * 0.3 + this.pointer.x * 0.85;
      tgt.lookY = tgt.lookY * 0.3 + this.pointer.y * 0.75;
    } else {
      tgt.lookX += this.gaze.x;
      tgt.lookY += this.gaze.y;
    }

    // ── the voice moves the mouth ──
    const lv = voice ? voice.level : 0;
    const a = lv > this.env ? 0.55 : 0.18; // fast attack, softer release
    this.env += (lv - this.env) * a;
    if (this.mode === 'speaking') {
      const open = clamp((this.env - 0.04) * 1.6, 0, 1);
      const roundness =
        voice && voice.centroid
          ? clamp((1700 - voice.centroid) / 1100, 0, 1)
          : 0.3;
      this.round += (roundness - this.round) * 0.25;
      tgt.mouthOpen = Math.max(tgt.mouthOpen * 0.35, open * 0.95);
      tgt.mouthRound = tgt.mouthRound * 0.4 + this.round * 0.6 * open;
      tgt.mouthWide *= 1 - 0.12 * open;
      tgt.browY += open * 0.12;
      tgt.tongue *= 0.2;
    }
    if (this.mode === 'listening' && mic > 0.08 && t > this.nextNod) {
      this.nod.v += 26;
      this.nextNod = t + rand(1.4, 2.8);
    }
    tgt.waves *=
      this.mode === 'listening' ? 0.35 + Math.min(1, mic * 1.6) * 0.65 : 1;

    const p = {};
    Object.keys(this.springs).forEach((k) => {
      p[k] = this.springs[k].step(tgt[k], dt);
    });

    // ── blinking ──
    let blink = 0;
    const b = this.blink;
    if (b.phase < 0 && t >= b.next) b.phase = 0;
    if (b.phase >= 0) {
      b.phase += dt;
      const slow = this.emotion === 'sleepy' ? 2.2 : 1;
      const close = 0.07 * slow;
      const hold = 0.035 * slow;
      const open = 0.12 * slow;
      if (b.phase < close) blink = b.phase / close;
      else if (b.phase < close + hold) blink = 1;
      else if (b.phase < close + hold + open)
        blink = 1 - (b.phase - close - hold) / open;
      else {
        b.phase = -1;
        if (!b.double && Math.random() < 0.18) {
          b.double = true;
          b.next = t + 0.09;
        } else {
          b.double = false;
          b.next =
            t + (this.emotion === 'sleepy' ? rand(1, 2.4) : rand(2.2, 5.6));
        }
      }
    }
    p.blink = blink;

    // ── head: breathing, sway, bounce, nods, squash & stretch ──
    const k = this.calm ? 0.35 : 1;
    const breathe = Math.sin(t * 1.25);
    let hx = (3 * Math.sin(t * 0.41) + 1.2 * Math.sin(t * 1.13 + 2)) * k;
    let hy = 2.2 * breathe * k;
    let tilt =
      p.tilt + (2.2 * Math.sin(t * 0.63) + 1.1 * Math.sin(t * 1.71 + 1)) * k;
    let sx = 1 - 0.008 * breathe * k;
    let sy = 1 + 0.014 * breathe * k;
    if (this.mode === 'speaking') {
      hy -= this.env * 3.5;
      tilt += this.env * 2 * Math.sin(t * 2.3);
    }
    if (p.bounce > 0.02 && !this.calm) {
      this.bouncePhase += dt * 7.2;
      const h = Math.abs(Math.sin(this.bouncePhase));
      hy -= h * 11 * p.bounce;
      sy += 0.05 * h * p.bounce;
      sx -= 0.03 * h * p.bounce;
      const land = Math.pow(1 - h, 10) * p.bounce;
      sy -= land * 0.06;
      sx += land * 0.05;
      p.lift = h * 11 * p.bounce;
    } else {
      p.lift = 0;
    }
    const nod = this.nod.step(0, dt);
    hy += nod * 0.08;
    const pop = this.pop.step(1, dt);
    sx *= 1 + (pop - 1) * 0.6;
    sy *= 2 - pop;
    sx *= 1 - p.shrink;
    sy *= 1 - p.shrink;
    if (this.shake > 0) {
      this.shake -= dt;
      hx += Math.sin(t * 34) * 7 * Math.max(0, this.shake / 0.55);
    }
    // the stem lags behind the head and wobbles
    const tiltVel = (tilt - this.prevTilt) / dt;
    const hxVel = (hx - this.prevHx) / dt;
    this.prevTilt = tilt;
    this.prevHx = hx;
    const stemTarget =
      -tiltVel * 0.35 -
      hxVel * 0.9 +
      5 * Math.sin(t * 0.9) * k +
      (this.mode === 'listening' ? mic * 14 : 0);
    p.stem = this.stem.step(clamp(stemTarget, -30, 30), dt);
    Object.assign(p, { hx, hy, tilt, sx, sy, t, env: this.env, mic });

    // ── hearts float up while in love ──
    if (p.hearts > 0.5) {
      this.heartClock -= dt;
      if (this.heartClock <= 0 && this.hearts.length < 6) {
        this.hearts.push({ x: rand(-85, 85), age: 0, seed: rand(0, 6) });
        this.heartClock = rand(0.35, 0.7);
      }
    }
    this.hearts.forEach((h) => {
      h.age += dt;
    });
    this.hearts = this.hearts.filter((h) => h.age < 2.3);
    p.heartList = this.hearts;
    return p;
  }
}
